import React, { useState } from 'react';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import { authApi } from '../../services/authApi';
import './AuthForm.css';

interface ChangePasswordFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess, onCancel }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validatePasswords = (): boolean => {
    if (newPassword.length < 6) {
      setValidationError('New password must be at least 6 characters long');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('New passwords do not match');
      return false;
    }
    if (currentPassword === newPassword) {
      setValidationError('New password must be different from current password');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!validatePasswords()) return;

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await authApi.changePassword(currentPassword, newPassword, confirmPassword);
      
      if (response.success) {
        setIsSuccess(true);
        setMessage('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        if (onSuccess) {
          setTimeout(() => onSuccess(), 1500);
        }
      } else {
        setIsSuccess(false);
        setMessage(response.message || 'Failed to change password');
      }
    } catch (error: any) {
      setIsSuccess(false);
      setMessage(error.message || 'Failed to change password. Please check your current password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
    if (validationError) setValidationError(null);
    if (message) setMessage(null);
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (validationError) setValidationError(null);
    if (message) setMessage(null);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (validationError) setValidationError(null);
    if (message) setMessage(null);
  };

  return (
    <div className="change-password-form-container">
      <h3 className="change-password-title">Change Password</h3>
      
      {(message || validationError) && (
        <div className={isSuccess ? 'auth-success-message' : 'auth-error-message'}>
          {validationError || message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        <FormGroup>
          <FormLabel htmlFor="currentPassword">
            Current Password
          </FormLabel>
          <FormInput
            type="password"
            id="currentPassword"
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
            required
            disabled={isSubmitting}
            placeholder="Enter current password"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="newPassword">
            New Password
          </FormLabel>
          <FormInput
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={handleNewPasswordChange}
            required
            disabled={isSubmitting}
            placeholder="Enter new password"
            minLength={6}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="confirmNewPassword">
            Confirm New Password
          </FormLabel>
          <FormInput
            type="password"
            id="confirmNewPassword"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required
            disabled={isSubmitting}
            placeholder="Confirm new password"
            minLength={6}
          />
        </FormGroup>

        <div className="change-password-actions">
          <FormButton
            type="submit"
            disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
            variant="primary"
          >
            {isSubmitting ? 'Changing...' : 'Change Password'}
          </FormButton>
          
          {onCancel && (
            <FormButton
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              variant="secondary"
            >
              Cancel
            </FormButton>
          )}
        </div>
      </form>
    </div>
  );
};
